import { ref, watch, computed } from '@vue/composition-api'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { useToast } from 'vue-toastification/composition'
import store from '@/store'
import router from '@/router/index'

// Notification

export default function useDashboardList() {
  // Use toast
  const toast = useToast()
  const route = router.history.current

  const refDashboardViewsTable = ref(null)

  // Table Handlers
  const tableColumns = [
    {
      key: 'name',
      label: 'dashboard view',
      sortable: true,
    },
    {
      key: 'departments',
      label: 'department',
      sortable: true,
    },
    {
      key: 'users',
      label: 'Users',
      sortable: true,
    },
    {
      key: 'status',
      label: 'status',
      sortable: true,
    },
    {
      key: 'createdAt',
      label: 'created on',
      sortable: true,
    },
    { key: 'actions' },
  ]
  const perPage = ref(route.query.perPage ?? process.env.VUE_APP_PER_PAGE_CONTENT)
  const totalDashboardViews = ref(0)
  const currentPage = ref(route.query.page ?? 1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref(route.query.search ?? '')
  const sortBy = ref(route.query.sortBy ?? 'createdAt')
  const isSortDirDesc = ref(route.query.sortDesc === 'true')
  const statusFilter = ref(route.query.status ?? null)
  const departments = ref([])
  if (Array.isArray(route.query.departments)) {
    departments.value = route.query.departments
  } else if (route.query.departments) {
    departments.value = [route.query.departments]
  }
  const users = ref([])
  if (Array.isArray(route.query.users)) {
    users.value = route.query.users
  } else if (route.query.users) {
    users.value = [route.query.users]
  }
  const createdAtFilter = ref(route.query.createdAt || '')
  const toDeletedID = ref(null)
  let initialLoad = true
  const currentDataLength = ref(0)

  const dataMeta = computed(() => {
    const localItemsCount = refDashboardViewsTable.value ? refDashboardViewsTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalDashboardViews.value,
    }
  })

  const refetchData = () => {
    refDashboardViewsTable.value.refresh()
  }

  watch([searchQuery, statusFilter, departments, users, createdAtFilter], () => {
    currentPage.value = 1
    refetchData()
  }, { deep: true })

  watch([currentPage], () => {
    refetchData()
  }, { deep: true })

  const fetchDashboardViews = (ctx, callback) => {
    store
      .dispatch('app-dashboard-views/fetchDashboardViews', {
        search: searchQuery.value,
        perPage: perPage.value,
        page: currentPage.value,
        sortBy: sortBy.value,
        sortDesc: isSortDirDesc.value,
        status: statusFilter.value,
        departments: departments.value,
        users: users.value,
        createdAt: createdAtFilter.value,
      },
      { root: true })
      .then(response => {
        const { views, total } = response.data
        currentDataLength.value = views.length
        callback(views)
        totalDashboardViews.value = total
        if (!initialLoad) {
          router.replace({
            query: {
              search: searchQuery.value,
              perPage: perPage.value,
              page: currentPage.value,
              sortBy: sortBy.value,
              sortDesc: isSortDirDesc.value,
              status: statusFilter.value,
              departments: departments.value,
              users: users.value,
              createdAt: createdAtFilter.value,
            },
          }).catch(() => {})
        }
        initialLoad = false
      })
      .catch(error => {
        toast({
          component: ToastificationContent,
          props: {
            title: error.data.message || process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const deleteDashboardView = () => {
    store
      .dispatch('app-dashboard-views/deleteDashboardView', {
        id: toDeletedID.value,
      },
      { root: true })
      .then(response => {
        refetchData()
        toast({
          component: ToastificationContent,
          props: {
            title: response.data.message ?? '',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
      .catch(error => {
        toast({
          component: ToastificationContent,
          props: {
            title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const resolveStatusVariant = status => {
    if (status === 'active') return 'success'
    if (status === 'inactive') return 'danger'
    return 'primary'
  }

  return {
    fetchDashboardViews,
    tableColumns,
    perPage,
    currentPage,
    totalDashboardViews,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refDashboardViewsTable,

    refetchData,
    resolveStatusVariant,

    toDeletedID,
    deleteDashboardView,
    currentDataLength,

    // Extra Filters
    statusFilter,
    departments,
    users,
    createdAtFilter,
  }
}
